import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, BadgeCheck, Heart, Gift, Headset, Send, ChevronLeft, ChevronRight, Lightbulb, Users, Store, MapPin, MoveRight } from "lucide-react";
import { heroSlides, trustBadges, LOGO_URL } from "../mock";

const NAVY = "#1B1F5C";

const iconMap = { ShieldCheck, BadgeCheck, Heart, Gift, Headset };

const Doodles = () => (
  <>
    <svg className="absolute top-10 left-4 md:left-8 w-8 h-8 text-[#35C3C1] animate-float z-10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
    <svg className="absolute bottom-24 left-2 md:left-10 w-9 h-9 text-[#F0426A] animate-float-delay z-10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
    <svg className="absolute top-8 left-[38%] hidden md:block w-6 h-6 text-[#F79433] animate-float z-10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
    <div className="absolute top-16 left-[42%] hidden lg:block z-10">
      <Send size={30} className="text-[#1B1F5C] -rotate-12" strokeWidth={1.6} />
      <svg width="120" height="90" viewBox="0 0 120 90" fill="none" className="absolute top-6 -left-16">
        <path d="M110 5 C60 15, 30 40, 10 85" stroke="#1B1F5C" strokeWidth="2" strokeDasharray="6 6" strokeLinecap="round" fill="none" />
      </svg>
    </div>
  </>
);

const franchiseStores = [
  {
    name: "KIOSK STORE",
    tagline: "Playful. Compact. Convenient.",
    pill: "#FCD44E",
    pos: "32% center",
    image: "https://customer-assets-v7afamib.emergentagent.net/job_appa-playtime/artifacts/yfip644z_Kiosk.png",
  },
  {
    name: "RETAIL STORE",
    tagline: "Bigger Play. Deeper Discovery.",
    pill: "#7EDCD6",
    image: "https://customer-assets-v7afamib.emergentagent.net/job_appa-playtime/artifacts/vvwc1ect_Flagship%20Store%20-%20Mall.png",
  },
  {
    name: "FLAGSHIP STORE",
    tagline: "A Complete World of Wonder.",
    pill: "#F6A8CE",
    image: "https://customer-assets-v7afamib.emergentagent.net/job_appa-playtime/artifacts/0qzkkkkd_Signature%20Store.png",
  },
];

const franchiseFeatures = [
  { icon: Lightbulb, text: "Curated Toys for Every Age" },
  { icon: ShieldCheck, text: "Safe & Quality Assured" },
  { icon: Users, text: "A Brighter Childhood for Every Family" },
  { icon: Store, text: "Now Closer to You" },
];

const Cloud = ({ className }) => (
  <svg className={className} viewBox="0 0 64 32" fill="currentColor"><path d="M20 28c-8 0-14-5-14-11 0-5 4-9 9-10C17 3 22 0 28 0c7 0 13 4 15 10 6 0 11 4 11 9s-5 9-11 9H20z"/></svg>
);

const FranchiseSlide = ({ slide }) => (
  <div className="h-full relative overflow-hidden" style={{ backgroundColor: "#FDF6EC" }}>
    {/* decorative layer */}
    <svg className="absolute top-6 left-[4%] w-6 h-6 text-[#FCD44E] z-10 animate-float" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
    <svg className="absolute top-24 left-[22%] hidden lg:block w-4 h-4 text-[#35C3C1] z-10 animate-float-delay" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
    <svg className="absolute bottom-6 right-[24%] hidden lg:block w-5 h-5 text-[#F79433] z-10 animate-float" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
    <Cloud className="absolute bottom-4 left-[2%] w-16 text-white/90 hidden lg:block" />
    <Cloud className="absolute top-10 right-[30%] w-12 text-white/80 hidden lg:block" />
    <div className="absolute top-5 left-[27%] hidden lg:block z-10">
      <Send size={26} className="text-[#1B1F5C] -rotate-12" strokeWidth={1.6} />
      <svg width="140" height="60" viewBox="0 0 140 60" fill="none" className="absolute top-5 left-6">
        <path d="M5 5 C50 10, 95 25, 135 55" stroke="#1B1F5C" strokeWidth="2" strokeDasharray="6 6" strokeLinecap="round" fill="none" opacity="0.5" />
      </svg>
    </div>
    <p className="hidden xl:block absolute top-5 right-[3%] font-display font-extrabold text-[16px] leading-[1.15] text-right rotate-3 z-10" style={{ color: "#1B1F5C" }}>
      Play<br />Learn<br />Grow<br /><span className="text-[#F79433]">Together!</span>
    </p>

    {/* full-width composition */}
    <div className="h-full w-full pl-5 pr-5 lg:pl-[5%] lg:pr-[3.5%] flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-[3%] pt-6 pb-12 lg:py-0">
      {/* LEFT content ~25% */}
      <div className="text-left shrink-0 w-full lg:w-[24%]" data-testid="franchise-banner-text">
        <img src={LOGO_URL} alt="Adhira & Appa Toys" className="hidden lg:block h-12 w-auto mb-2 -ml-1" />
        <span className="inline-block mb-2.5 px-3.5 py-1 lg:px-4 lg:py-1.5 rounded-full text-[10px] lg:text-[10.5px] font-extrabold tracking-widest text-white" style={{ backgroundColor: "#35C3C1" }}>
          {slide.badge}
        </span>
        <h1 className="font-display font-extrabold leading-[0.95] tracking-tight">
          {slide.titleLines.map((l) => (
            <span key={l.t} className="block text-[32px] sm:text-4xl lg:text-[36px] xl:text-[44px]" style={{ color: l.c }}>{l.t}</span>
          ))}
        </h1>
        <p className="mt-2.5 text-[12px] lg:text-[13px] font-semibold text-[#4A4A6A] max-w-xs leading-snug">
          {slide.desc}
        </p>
        <div className="mt-3.5 flex flex-wrap items-center gap-2.5">
          <Link
            to={slide.primaryCta.to}
            data-testid="franchise-banner-primary-cta"
            className="px-5 h-10 lg:h-11 rounded-full text-white text-[11px] font-extrabold tracking-widest shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-transform duration-200 inline-flex items-center gap-1.5"
            style={{ backgroundColor: NAVY }}
          >
            {slide.primaryCta.label} <MoveRight size={14} />
          </Link>
          <Link
            to={slide.secondaryCta.to}
            data-testid="franchise-banner-secondary-cta"
            className="px-5 h-10 lg:h-11 rounded-full text-[11px] font-extrabold tracking-widest border-2 border-[#35C3C1] text-[#1B1F5C] bg-white/70 hover:bg-[#35C3C1] hover:text-white transition-colors duration-200 inline-flex items-center"
          >
            {slide.secondaryCta.label}
          </Link>
        </div>
      </div>

      {/* RIGHT: three large store visuals ~70% + benefits */}
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-3 lg:gap-4">
        <div className="flex lg:grid lg:grid-cols-3 gap-4 xl:gap-5 overflow-x-auto no-scrollbar -mx-5 px-5 lg:mx-0 lg:px-0 snap-x snap-mandatory">
          {franchiseStores.map((s) => (
            <Link to="/franchise" key={s.name} data-testid={`franchise-store-card-${s.name.toLowerCase().replace(/\s+/g, "-")}`} className="relative shrink-0 w-[250px] sm:w-[290px] lg:w-auto snap-start group text-center">
              <div className="rounded-2xl lg:rounded-3xl overflow-hidden bg-white" style={{ boxShadow: "0 14px 34px -12px rgba(27,31,92,0.28)" }}>
                <img src={s.image} alt={s.name} className="h-36 sm:h-44 lg:h-[240px] xl:h-[280px] w-full object-cover group-hover:scale-[1.03] transition-transform duration-500" style={{ objectPosition: s.pos || "center" }} loading="lazy" />
              </div>
              <div className="-mt-5 relative z-10 mx-auto w-[78%] lg:w-[72%]">
                <p className="h-9 lg:h-10 rounded-full flex items-center justify-center text-[11px] lg:text-[13px] font-extrabold tracking-wider shadow-md" style={{ backgroundColor: s.pill, color: NAVY }}>
                  {s.name}
                </p>
              </div>
              <p className="mt-1.5 lg:mt-2 flex items-center justify-center gap-1 text-[11px] lg:text-[12.5px] font-bold" style={{ color: NAVY }}>
                <MapPin size={13} className="text-[#35C3C1] shrink-0" /> {s.tagline}
              </p>
            </Link>
          ))}
        </div>
        <div className="hidden sm:flex items-center justify-center flex-wrap gap-x-3 gap-y-2 lg:gap-x-0 lg:divide-x lg:divide-[#E3D9C6]">
          {franchiseFeatures.map((f) => (
            <div key={f.text} className="flex items-center gap-2 lg:px-4 xl:px-6">
              <span className="h-8 w-8 lg:h-9 lg:w-9 rounded-full bg-[#E7F8F8] flex items-center justify-center shrink-0">
                <f.icon size={15} style={{ color: NAVY }} strokeWidth={1.8} />
              </span>
              <p className="text-[10.5px] lg:text-[11.5px] font-bold max-w-[118px] text-left leading-tight" style={{ color: NAVY }}>{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const Hero = () => {
  const [current, setCurrent] = useState(0);
  const total = heroSlides.length;

  const next = useCallback(() => setCurrent((c) => (c + 1) % total), [total]);
  const prev = () => setCurrent((c) => (c + total - 1) % total);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, current]);

  return (
    <section className="relative overflow-hidden" style={{ backgroundColor: "#FDF9F2" }}>
      {/* Carousel */}
      <div className="relative hero-carousel h-[600px] sm:h-[540px] lg:h-[520px]">
        {heroSlides.map((slide, i) => (
          <div
            key={slide.id}
            className="absolute inset-0"
            style={{
              opacity: i === current ? 1 : 0,
              visibility: i === current ? "visible" : "hidden",
              transition: "opacity 700ms ease, visibility 700ms ease",
            }}
            aria-hidden={i !== current}
          >
            {slide.id === "franchise" ? (
              <FranchiseSlide slide={slide} />
            ) : (
            <>
            {/* ---------- Mobile / tablet: stacked layout ---------- */}
            <div className="h-full flex flex-col lg:hidden">
              <div className="px-5 pt-7 pb-5 text-left">
                {slide.badge && (
                  <span className="inline-block mb-3 px-3.5 py-1 rounded-full text-[10px] font-extrabold tracking-widest text-white" style={{ backgroundColor: "#35C3C1" }}>
                    {slide.badge}
                  </span>
                )}
                <h1 className="font-display font-extrabold leading-[0.95] tracking-tight">
                  {slide.titleLines.map((l) => (
                    <span key={l.t} className="block text-[38px] sm:text-5xl" style={{ color: l.c }}>{l.t}</span>
                  ))}
                </h1>
                <p className="mt-3 text-[13px] sm:text-[14px] font-semibold text-[#4A4A6A] max-w-sm leading-relaxed">
                  {slide.desc}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {slide.primaryCta.enquiry ? (
                    <button
                      onClick={() => window.dispatchEvent(new CustomEvent("open-enquiry"))}
                      data-testid="hero-enquire-cta-mobile"
                      className="px-5 h-10 rounded-full text-white text-[11.5px] font-extrabold tracking-widest shadow-lg inline-flex items-center"
                      style={{ backgroundColor: NAVY }}
                    >
                      {slide.primaryCta.label}
                    </button>
                  ) : (
                    <Link
                      to={slide.primaryCta.to}
                      className="px-5 h-10 rounded-full text-white text-[11.5px] font-extrabold tracking-widest shadow-lg inline-flex items-center"
                      style={{ backgroundColor: NAVY }}
                    >
                      {slide.primaryCta.label}
                    </Link>
                  )}
                  <Link
                    to={slide.secondaryCta.to}
                    className="px-5 h-10 rounded-full text-[11.5px] font-extrabold tracking-widest border-2 border-[#35C3C1] text-[#1B1F5C] bg-white/60 inline-flex items-center"
                  >
                    {slide.secondaryCta.label}
                  </Link>
                </div>
              </div>
              <div className="relative flex-1 min-h-[180px] mx-4 mb-5 rounded-2xl overflow-hidden shadow-sm">
                <img
                  src={slide.image}
                  alt={slide.titleLines.map((l) => l.t).join(" ")}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: slide.imagePosition }}
                />
              </div>
            </div>

            {/* ---------- Desktop: full-bleed overlay layout ---------- */}
            <div className="hidden lg:block h-full relative">
              <img
                src={slide.image}
                alt={slide.titleLines.map((l) => l.t).join(" ")}
                className="absolute inset-0 w-full h-full object-cover object-center"
                style={{ objectPosition: slide.imagePosition }}
              />
              {/* left legibility overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#FDF9F2]/50 via-transparent to-transparent pointer-events-none" />

              <div className="relative z-10 max-w-7xl mx-auto px-4 h-full flex items-center">
                <div className="max-w-lg text-left">
                  {slide.badge && (
                    <span className="inline-block mb-3 px-4 py-1.5 rounded-full text-[11px] font-extrabold tracking-widest text-white" style={{ backgroundColor: "#35C3C1" }}>
                      {slide.badge}
                    </span>
                  )}
                  <h1 className="font-display font-extrabold leading-[0.95] tracking-tight">
                    {slide.titleLines.map((l) => (
                      <span key={l.t} className="block text-5xl xl:text-6xl" style={{ color: l.c }}>{l.t}</span>
                    ))}
                  </h1>
                  <p className="mt-3.5 text-base font-semibold text-[#4A4A6A] max-w-sm leading-relaxed">
                    {slide.desc}
                  </p>
                  <div className="mt-5 flex flex-wrap items-center gap-4">
                    {slide.primaryCta.enquiry ? (
                      <button
                        onClick={() => window.dispatchEvent(new CustomEvent("open-enquiry"))}
                        data-testid="hero-enquire-cta-desktop"
                        className="px-6 h-10 rounded-full text-white text-[11.5px] font-extrabold tracking-widest shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-transform duration-200 inline-flex items-center"
                        style={{ backgroundColor: NAVY }}
                      >
                        {slide.primaryCta.label}
                      </button>
                    ) : (
                      <Link
                        to={slide.primaryCta.to}
                        className="px-6 h-10 rounded-full text-white text-[11.5px] font-extrabold tracking-widest shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-transform duration-200 inline-flex items-center"
                        style={{ backgroundColor: NAVY }}
                      >
                        {slide.primaryCta.label}
                      </Link>
                    )}
                    <Link
                      to={slide.secondaryCta.to}
                      className="px-6 h-10 rounded-full text-[11.5px] font-extrabold tracking-widest border-2 border-[#35C3C1] text-[#1B1F5C] bg-white/60 hover:bg-[#35C3C1] hover:text-white transition-colors duration-200 inline-flex items-center"
                    >
                      {slide.secondaryCta.label}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            </>
            )}
          </div>
        ))}

        {heroSlides[current].id !== "franchise" && <div className="hidden lg:block"><Doodles /></div>}

        {/* Arrows (desktop only) */}
        <button
          onClick={prev}
          className="hidden lg:flex absolute z-20 left-3 md:left-5 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 shadow-lg items-center justify-center hover:scale-110 transition-transform"
          style={{ color: NAVY }}
          aria-label="Previous slide"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={next}
          className="hidden lg:flex absolute z-20 right-3 md:right-5 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 shadow-lg items-center justify-center hover:scale-110 transition-transform"
          style={{ color: NAVY }}
          aria-label="Next slide"
        >
          <ChevronRight size={20} />
        </button>

        {/* Dots */}
        <div className="absolute z-20 bottom-8 lg:bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {heroSlides.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setCurrent(i)}
              className="rounded-full transition-colors"
              style={{
                height: 8,
                width: i === current ? 26 : 8,
                backgroundColor: i === current ? NAVY : "rgba(27,31,92,0.35)",
                transition: "width 300ms ease, background-color 300ms ease",
              }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Trust badges strip - single responsive strip on all screens */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 pb-8 mt-2 lg:-mt-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 flex items-center overflow-x-auto no-scrollbar lg:grid lg:grid-cols-5 lg:divide-x lg:divide-gray-100">
          {trustBadges.map((b) => {
            const Icon = iconMap[b.icon];
            return (
              <div key={b.line1} className="flex items-center gap-2.5 lg:gap-3 px-4 py-3 lg:px-5 lg:py-4 justify-center shrink-0">
                <span className="h-9 w-9 lg:h-10 lg:w-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#EEF0FA" }}>
                  <Icon size={17} style={{ color: NAVY }} strokeWidth={1.8} />
                </span>
                <div className="text-left leading-tight whitespace-nowrap">
                  <p className="text-[11.5px] lg:text-[12.5px] font-extrabold" style={{ color: NAVY }}>{b.line1}</p>
                  <p className="text-[11.5px] lg:text-[12.5px] font-extrabold" style={{ color: NAVY }}>{b.line2}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Hero;
